import React from 'react';
import {
  PostDetails_ActivePost_Fragment,
  PostDetails_DeletedPost_Fragment,
  PostDetailsFragment,
  PostOrder,
  PrivatePostsQueryResponse,
  usePrivatePostsQuery,
  UserPostsQueryResponse,
  useUserPostsQuery,
} from '../__generated__/api.graphql';
import {
  CLINICIANS_AGENT_USERNAME,
  MEMBERS_AGENT_USERNAME,
} from '../constants';
import {
  useActiveProjectCohortId,
  useActiveProjectId,
  useActiveUserId,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { QueryResult } from 'react-apollo';

const usePostHistory = (
  projectId: string,
  cohortId: string,
  userId: string | undefined,
  patientId: string | undefined,
  patientUsername: string | undefined,
  agentUsername: string,
  userIdsOverride?: string[],
  isMemberChatHistory?: boolean,
):
  | QueryResult<UserPostsQueryResponse>
  | QueryResult<PrivatePostsQueryResponse> => {
  const resolvedPrivateUserIds =
    userIdsOverride ||
    ([
      userId,
      agentUsername,
      ...(patientId ? [`scope?subject=${patientId}`] : []),
    ].filter(Boolean) as string[]);
  const userPostResult = useUserPostsQuery({
    variables: {
      userIds: [MEMBERS_AGENT_USERNAME, patientUsername || ''],
      projectId: projectId,
      cohortId: cohortId,
      filter: {
        order: PostOrder.NEWEST,
      },
    },
    skip: !isMemberChatHistory,
  }) as QueryResult<UserPostsQueryResponse>;

  const privatePostResult = usePrivatePostsQuery({
    variables: {
      userIds: resolvedPrivateUserIds,
      filter: {
        order: PostOrder.NEWEST,
      },
    },
    skip: isMemberChatHistory || resolvedPrivateUserIds.length === 0,
  }) as QueryResult<PrivatePostsQueryResponse>;
  return isMemberChatHistory ? userPostResult : privatePostResult;
};

type MappedPosts = {
  posts: PostDetailsFragment[];
  endCursor: string | null | undefined;
  hasNextPage: boolean;
};

const mapResToPosts = (
  queryResponse: PrivatePostsQueryResponse | UserPostsQueryResponse,
): MappedPosts => {
  type NodeType =
    | ({ __typename: 'ActivePost' } & PostDetails_ActivePost_Fragment)
    | ({ __typename: 'DeletedPost' } & PostDetails_DeletedPost_Fragment);
  type EdgeType = {
    __typename: 'PostEdge';
    node: NodeType;
  };
  const connection =
    'privatePosts' in queryResponse
      ? queryResponse.privatePosts
      : queryResponse.userPosts;

  const posts = connection.edges
    .map((edge: EdgeType) => edge?.node)
    .filter((node: NodeType): node is PostDetailsFragment => !!node);

  return {
    posts,
    endCursor: connection.pageInfo.endCursor,
    hasNextPage: connection.pageInfo.hasNextPage,
  };
};

export const useChatHistory = ({
  patientId,
  isMemberChatHistory,
  patientUsername,
  agentUsername = CLINICIANS_AGENT_USERNAME,
  userIdsOverride,
}: {
  patientId?: string;
  isMemberChatHistory?: boolean;
  patientUsername?: string;
  agentUsername?: string;
  userIdsOverride?: string[];
}) => {
  const userId = useActiveUserId();
  const projectId = useActiveProjectId();
  const cohortId = useActiveProjectCohortId();
  const [posts, setPosts] = React.useState<PostDetailsFragment[]>([]);
  const initialPageLoaded = React.useRef(false);
  const [endCursor, setEndCursor] = React.useState<string | undefined | null>(
    null,
  );
  const [hasNextPage, setHasNextPage] = React.useState<boolean>(false);
  const { data, loading, fetchMore, networkStatus, startPolling } =
    usePostHistory(
      projectId,
      cohortId,
      userId,
      patientId,
      patientUsername,
      agentUsername,
      userIdsOverride,
      isMemberChatHistory,
    );
  const historyScopeKey = React.useMemo(() => {
    if (isMemberChatHistory) {
      return [
        'member',
        projectId,
        cohortId,
        MEMBERS_AGENT_USERNAME,
        patientUsername || '',
      ].join('|');
    }

    const privateUserIds =
      userIdsOverride ||
      ([
        userId,
        agentUsername,
        ...(patientId ? [`scope?subject=${patientId}`] : []),
      ].filter(Boolean) as string[]);

    return ['private', ...[...privateUserIds].sort()].join('|');
  }, [
    agentUsername,
    cohortId,
    isMemberChatHistory,
    patientId,
    patientUsername,
    projectId,
    userId,
    userIdsOverride,
  ]);

  React.useEffect(() => {
    initialPageLoaded.current = false;
    setPosts([]);
    setEndCursor(null);
    setHasNextPage(false);
  }, [historyScopeKey]);

  // Sync local state from the live query. Three things can drive an update
  // here: (1) the initial page load, (2) the 3s poll picking up newly arrived
  // messages, and (3) a fetchMore that broadens the cached connection. We
  // only ever *prepend* messages here — older pages loaded via `loadMore`
  // are appended in that function explicitly.
  React.useEffect(() => {
    if (!data) return;
    const {
      posts: latestPosts,
      endCursor: latestEndCursor,
      hasNextPage: latestHasNextPage,
    } = mapResToPosts(data);

    if (!initialPageLoaded.current) {
      setPosts(latestPosts);
      setEndCursor(latestEndCursor);
      setHasNextPage(latestHasNextPage);
      initialPageLoaded.current = true;
      return;
    }

    setPosts(currentPosts => {
      const knownIds = new Set(currentPosts.map(post => post.id));
      const newArrivals = latestPosts.filter(post => !knownIds.has(post.id));
      if (newArrivals.length === 0) return currentPosts;
      // Posts are sorted newest-first; anything we haven't seen from the
      // live first-page query is newer than what's at the head of our list.
      return [...newArrivals, ...currentPosts];
    });
  }, [data]);

  const loadMore = async (): Promise<void> => {
    const privatePostUserIds =
      userIdsOverride ||
      ([
        userId,
        agentUsername,
        ...(patientId ? [`scope?subject=${patientId}`] : []),
      ].filter(Boolean) as string[]);
    if (!isMemberChatHistory && privatePostUserIds.length === 0) {
      return;
    }
    if (!hasNextPage || !endCursor) {
      return;
    }

    const params = isMemberChatHistory
      ? {
          variables: {
            userIds: [MEMBERS_AGENT_USERNAME, patientUsername || ''],
            projectId: projectId,
            cohortId: cohortId,
            filter: { order: PostOrder.NEWEST },
            after: endCursor,
          },
        }
      : {
          variables: {
            userIds: privatePostUserIds,
            filter: {
              order: PostOrder.NEWEST,
            },
            after: endCursor,
          },
        };

    const result = await fetchMore(params);

    const {
      posts: morePosts,
      endCursor: newEndCursor,
      hasNextPage: newHasNextPage,
    } = mapResToPosts(result.data);

    setPosts(currentPosts => {
      const knownIds = new Set(currentPosts.map(post => post.id));
      const dedupedMore = morePosts.filter(post => !knownIds.has(post.id));
      if (dedupedMore.length === 0) return currentPosts;
      return [...currentPosts, ...dedupedMore];
    });
    setEndCursor(newEndCursor);
    setHasNextPage(newHasNextPage);
  };

  /** Eagerly pushes a new post into the posts array for faster UI responsiveness on sending a message. */
  const addEagerPost = (post: PostDetailsFragment) => {
    setPosts(posts => [post, ...posts]);
  };

  return {
    loading,
    fetchMore,
    loadMore,
    addEagerPost,
    posts,
    endCursor,
    hasNextPage,
    networkStatus,
    userId,
    startPolling,
  };
};
